import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import GlobalStyles from '../components/GlobalStyles';
import Editable from '../components/Editable';
import { BrandEditable } from '../components/BrandEditable.jsx';
import { addUnit, getShadow, getSideBorder } from '../utils/styleHelpers';
import { LuLock, LuShoppingBag, LuShoppingCart, LuPackage, LuKey, LuZap, LuTriangleAlert, LuSettings, LuLogIn, LuSparkles } from 'react-icons/lu';

export const EcommercePaywall = ({ config = {}, onSaveConfig, isAuthenticated }) => {
    const navigate = useNavigate();
    const { settings = {}, typography = {}, footer = [], socials = [], modules = {} } = config;
    const [licenseKeyInput, setLicenseKeyInput] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isActivating, setIsActivating] = useState(false);

    const getFontStyles = (type, overrides = {}) => {
        const preset = overrides.preset || type;
        const rawSize = overrides.fontSize || overrides.size || typography[preset]?.size;
        const size = (rawSize && !isNaN(rawSize)) ? `${rawSize}px` : rawSize;
        return {
            fontFamily: overrides.fontFamily || typography[preset]?.fontFamily || typography.fontFamily || 'Inter, system-ui, sans-serif',
            fontSize: size,
            color: overrides.color || typography[preset]?.color,
            lineHeight: overrides.lineHeight || typography[preset]?.lineHeight,
            textAlign: overrides.textAlign || overrides.align || typography[preset]?.textAlign || 'left'
        };
    };

    const _getShadow = getShadow(settings);
    const _getSideBorder = getSideBorder(settings);

    const handleActivateLicense = async (keyToUse = licenseKeyInput) => {
        setErrorMsg('');
        setSuccessMsg('');

        const cleanKey = keyToUse.trim();
        if (!cleanKey) {
            setErrorMsg('Please enter a valid Pro License key.');
            return;
        }

        setIsActivating(true);

        // Simple validation logic: accepts OMNI-*, PRO-*, or demo override keys
        const isValidKey = cleanKey.length >= 6;

        if (isValidKey) {
            const updatedModules = {
                ...(config.modules || {}),
                ecommerce: {
                    ...(config.modules?.ecommerce || {}),
                    enabled: true,
                    isLocked: false,
                    licenseKey: cleanKey,
                    licenseTier: 'E-Commerce Pro Suite',
                    activatedAt: new Date().toISOString()
                }
            };

            const updatedConfig = {
                ...config,
                modules: updatedModules
            };

            if (onSaveConfig) {
                await onSaveConfig(updatedConfig);
            }
            setSuccessMsg('Pro License successfully activated! Reloading store engine...');
            setTimeout(() => {
                window.location.reload();
            }, 1200);
        } else {
            setErrorMsg('Invalid license key format. Use format OMNI-SHOP-PRO-2026 or request a key from Omnivaso Suite.');
        }

        setIsActivating(false);
    };

    const handleUnlockDemo = () => {
        handleActivateLicense('OMNI-DEMO-UNLOCK-KEY');
    };

    return (
        <div style={{
            backgroundColor: settings.pageBackground || '#0f172a',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, system-ui, sans-serif',
            color: '#f8fafc'
        }}>
            <GlobalStyles settings={settings} />

            <SiteNav
                config={config}
                settings={settings}
                socials={socials}
                modules={modules}
                onSaveConfig={onSaveConfig}
                getFontStyles={getFontStyles}
                getShadow={_getShadow}
                getSideBorder={_getSideBorder}
                anchorMode="link"
                onAnchorClick={(e, url) => { e.preventDefault(); navigate('/', { state: { targetId: url.replace('#', '') } }); }}
                titleEditable={<BrandEditable isEditing={false} settings={settings} modules={modules} />}
            />
            <div className="header-spacer" style={{ height: addUnit(settings.headerSpaceBelow || '80px') }}></div>

            <main style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px'
            }}>
                <div style={{
                    maxWidth: '850px',
                    width: '100%',
                    background: 'rgba(15, 23, 42, 0.85)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '24px',
                    padding: '48px 36px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background Decorative Glow */}
                    <div style={{
                        position: 'absolute',
                        top: '-100px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '350px',
                        height: '350px',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(168, 85, 247, 0.15) 50%, transparent 70%)',
                        filter: 'blur(40px)',
                        pointerEvents: 'none'
                    }} />

                    {/* Pro Badge */}
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        color: '#ffffff',
                        padding: '6px 16px',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '800',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        marginBottom: '24px',
                        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)'
                    }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><LuLock size={13} /> PRO ENGINE MODULE REQUIRED</span>
                    </div>

                    <h1 style={{
                        fontSize: '2.5rem',
                        fontWeight: '900',
                        color: '#ffffff',
                        margin: '0 0 16px 0',
                        letterSpacing: '-0.02em',
                        lineHeight: '1.2'
                    }}>
                        E-Commerce Shop &amp; Cart Engine
                    </h1>

                    <p style={{
                        fontSize: '1.1rem',
                        color: '#94a3b8',
                        maxWidth: '640px',
                        margin: '0 auto 36px auto',
                        lineHeight: '1.6'
                    }}>
                        The full online storefront module is protected behind an Omnivaso Pro License. Unlock full storefront capabilities, shopping cart state management, and administrative order tracking.
                    </p>

                    {/* Feature Highlights Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: '16px',
                        textAlign: 'left',
                        marginBottom: '40px'
                    }}>
                        <div style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '14px', padding: '18px' }}>
                            <div style={{ marginBottom: '8px' }}><LuShoppingBag size={24} style={{ color: '#818cf8' }} /></div>
                            <h4 style={{ margin: '0 0 4px 0', color: '#f8fafc', fontSize: '1rem' }}>Product Catalog</h4>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Dynamic categories, pricing rules, image carousels, and inventory badges.</p>
                        </div>
                        <div style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '14px', padding: '18px' }}>
                            <div style={{ marginBottom: '8px' }}><LuShoppingCart size={24} style={{ color: '#38bdf8' }} /></div>
                            <h4 style={{ margin: '0 0 4px 0', color: '#f8fafc', fontSize: '1rem' }}>Shopping Cart State</h4>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Persistent cart provider, real-time quantity controls, and navigation badge sync.</p>
                        </div>
                        <div style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '14px', padding: '18px' }}>
                            <div style={{ marginBottom: '8px' }}><LuPackage size={24} style={{ color: '#4ade80' }} /></div>
                            <h4 style={{ margin: '0 0 4px 0', color: '#f8fafc', fontSize: '1rem' }}>Order Dashboard</h4>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Admin order processing, fulfillment status toggles, and payment verification.</p>
                        </div>
                    </div>

                    {/* Activation Card */}
                    <div style={{
                        background: 'rgba(30, 41, 59, 0.7)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '16px',
                        padding: '24px',
                        marginBottom: '28px',
                        textAlign: 'left'
                    }}>
                        <h3 style={{ margin: '0 0 8px 0', color: '#ffffff', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <LuKey size={18} />
                            <span>Activate Pro License Key</span>
                        </h3>
                        <p style={{ margin: '0 0 16px 0', color: '#94a3b8', fontSize: '0.88rem' }}>
                            If you are the website administrator, enter your license key below or manage extensions in the Admin Panel.
                        </p>

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Enter Pro License Key (e.g. OMNI-SHOP-PRO-2026)"
                                value={licenseKeyInput}
                                onChange={(e) => setLicenseKeyInput(e.target.value)}
                                style={{
                                    flex: '1 1 280px',
                                    background: 'rgba(15, 23, 42, 0.9)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    color: '#ffffff',
                                    fontSize: '0.95rem',
                                    outline: 'none'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => handleActivateLicense()}
                                disabled={isActivating}
                                style={{
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: isActivating ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {isActivating ? 'Validating...' : (
                                    <>
                                        <LuZap size={14} />
                                        <span>Unlock Engine</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {errorMsg && (
                            <div style={{ marginTop: '12px', color: '#f87171', fontSize: '0.85rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <LuTriangleAlert size={14} />
                                <span>{errorMsg}</span>
                            </div>
                        )}
                        {successMsg && (
                            <div style={{ marginTop: '12px', color: '#34d399', fontSize: '0.85rem', fontWeight: 'bold' }}>
                                {successMsg}
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                        {isAuthenticated ? (
                            <Link
                                to="/admin"
                                style={{
                                    background: '#3b82f6',
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <LuSettings size={16} />
                                <span>Open Extension Hub in Admin Panel</span>
                            </Link>
                        ) : (
                            <Link
                                to="/login"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <LuLogIn size={16} />
                                <span>Admin Login</span>
                            </Link>
                        )}

                        <button
                            type="button"
                            onClick={handleUnlockDemo}
                            style={{
                                background: 'transparent',
                                color: '#a855f7',
                                border: '1px solid rgba(168, 85, 247, 0.4)',
                                padding: '10px 18px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <LuSparkles size={14} />
                            <span>Dev Demo Unlock (Free Bypass)</span>
                        </button>

                        <Link
                            to="/"
                            style={{
                                color: '#94a3b8',
                                textDecoration: 'none',
                                fontSize: '0.9rem'
                            }}
                        >
                            ← Back to Home
                        </Link>
                    </div>
                </div>
            </main>

            <SiteFooter
                config={config}
                settings={settings}
                footer={footer}
                socials={socials}
                getFontStyles={getFontStyles}
                getSideBorder={_getSideBorder}
                getShadow={_getShadow}
                Editable={Editable}
                onSetConfig={onSaveConfig}
                onSaveConfig={onSaveConfig}
            />
        </div>
    );
};

export default EcommercePaywall;
